import type { EnumOption, FormLocale, FormSchema, I18nText, JsonType, PropertySchema, UiField, UiSchema, UiStep } from "./types";

/** Resolves bilingual text for a locale, falling back to English, then to the other language. */
export function localize(text: I18nText | undefined, locale: FormLocale): string {
  if (text === undefined || text === null) return "";
  if (typeof text === "string") return text;
  return text[locale] ?? text.en ?? text.ar ?? "";
}

/** Returns the label for a schema node: `x-ncp.title` (bilingual) → `title` → last path segment humanised. */
export function labelOf(schema: PropertySchema | undefined, path: string, locale: FormLocale): string {
  const x = schema?.["x-ncp"];
  return localize(x?.title, locale) || schema?.title || humanise(lastSegment(path));
}

export function descriptionOf(schema: PropertySchema | undefined, locale: FormLocale): string {
  return localize(schema?.["x-ncp"]?.description, locale) || schema?.description || "";
}

export function humanise(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function lastSegment(path: string): string {
  const parts = path.split(".");
  return parts[parts.length - 1] ?? path;
}

/** Splits `a.b[0].c` / `a.b.0.c` into segments. Numeric segments become numbers. */
export function segments(path: string): Array<string | number> {
  return path
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter((s) => s.length > 0)
    .map((s) => (/^\d+$/.test(s) ? Number(s) : s));
}

export function getAtPath(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const seg of segments(path)) {
    if (cur === null || cur === undefined) return undefined;
    cur = (cur as Record<string | number, unknown>)[seg];
  }
  return cur;
}

export function setAtPath<T extends Record<string, unknown>>(obj: T, path: string, value: unknown): T {
  const segs = segments(path);
  const root: Record<string | number, unknown> = { ...obj };
  let cur: Record<string | number, unknown> = root;
  segs.forEach((seg, i) => {
    if (i === segs.length - 1) {
      cur[seg] = value;
      return;
    }
    const next = cur[seg];
    const copy = Array.isArray(next) ? [...next] : typeof next === "object" && next !== null ? { ...(next as Record<string, unknown>) } : typeof segs[i + 1] === "number" ? [] : {};
    cur[seg] = copy;
    cur = copy as Record<string | number, unknown>;
  });
  return root as T;
}

/** Walks the schema following a dot path (array indices skip into `items`). */
export function schemaAtPath(schema: PropertySchema, path: string): PropertySchema | undefined {
  let cur: PropertySchema | undefined = schema;
  for (const seg of segments(path)) {
    if (!cur) return undefined;
    if (typeof seg === "number" || seg === "*") cur = cur.items;
    else cur = cur.properties?.[seg];
  }
  return cur;
}

export function primaryType(schema: PropertySchema | undefined): JsonType | undefined {
  if (!schema?.type) {
    if (schema?.properties) return "object";
    if (schema?.items) return "array";
    if (schema?.enum) return "string";
    return undefined;
  }
  return Array.isArray(schema.type) ? schema.type.find((t) => t !== ("null" as unknown)) : schema.type;
}

export interface FlatField {
  path: string;
  schema: PropertySchema;
  type: JsonType | undefined;
  required: boolean;
  depth: number;
  label: string;
}

/** Flattens object properties (recursing into nested objects, not into array items) into a list ordered as declared. */
export function flattenFields(schema: FormSchema, locale: FormLocale = "en"): FlatField[] {
  const out: FlatField[] = [];
  const walk = (node: PropertySchema, prefix: string, depth: number) => {
    const required = new Set(node.required ?? []);
    for (const [key, child] of Object.entries(node.properties ?? {})) {
      const path = prefix ? `${prefix}.${key}` : key;
      const type = primaryType(child);
      out.push({ path, schema: child, type, required: required.has(key), depth, label: labelOf(child, path, locale) });
      if (type === "object" && child.properties && !child["x-ncp"]?.widget) walk(child, path, depth + 1);
    }
  };
  walk(schema, "", 0);
  return out;
}

/** Counts leaf inputs (objects rendered by a composite widget count once). */
export function countFields(schema: FormSchema): number {
  return flattenFields(schema).filter((f) => f.type !== "object" || f.schema["x-ncp"]?.widget).length;
}

/** Builds the initial value for a schema: `default`s, empty arrays and nested objects. */
export function defaultsFor(schema: PropertySchema | undefined): unknown {
  if (!schema) return undefined;
  if (schema.default !== undefined) return structuredClone(schema.default);
  switch (primaryType(schema)) {
    case "object": {
      const o: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(schema.properties ?? {})) {
        const d = defaultsFor(v);
        if (d !== undefined) o[k] = d;
      }
      return o;
    }
    case "array":
      return [];
    case "boolean":
      return undefined;
    default:
      return undefined;
  }
}

/** Every field path placed in a step (used for per-step validation). */
export function pathsInStep(step: UiStep): string[] {
  return step.sections.flatMap((s) => s.fields.filter((f) => !f.content).map((f) => f.path));
}

/** Paths referenced by the UI schema that do not exist in the data schema. */
export function orphanPaths(schema: FormSchema, ui: UiSchema): string[] {
  const missing: string[] = [];
  for (const step of ui.steps) for (const f of pathsInStep(step)) if (!schemaAtPath(schema, f)) missing.push(f);
  return missing;
}

/** Schema properties not placed anywhere in the UI schema (they still validate, but the applicant cannot fill them). */
export function unplacedPaths(schema: FormSchema, ui: UiSchema): string[] {
  const placed = new Set(ui.steps.flatMap(pathsInStep));
  const isCoveredByParent = (path: string) => [...placed].some((p) => path.startsWith(p + "."));
  return flattenFields(schema)
    .filter((f) => !(f.type === "object" && !f.schema["x-ncp"]?.widget))
    .map((f) => f.path)
    .filter((p) => !placed.has(p) && !isCoveredByParent(p));
}

/** Options for an enum property, with bilingual labels when supplied. */
export function enumOptionsOf(schema: PropertySchema | undefined, locale: FormLocale): Array<{ value: string; label: string; description?: string; deprecated?: boolean }> {
  if (!schema) return [];
  const x = schema["x-ncp"];
  const labelled = new Map<string, EnumOption>((x?.enumOptions ?? []).map((o) => [String(o.value), o]));
  const values = schema.enum ?? schema.items?.enum ?? (x?.enumOptions ?? []).map((o) => o.value);
  return values
    .filter((v) => v !== null)
    .map((v) => {
      const key = String(v);
      const o = labelled.get(key);
      return { value: key, label: o ? localize(o.label, locale) || key : humanise(key), description: o?.description ? localize(o.description, locale) : undefined, deprecated: o?.deprecated };
    });
}

/** Deep-clones a JSON-like value. */
export function clone<T>(v: T): T {
  return v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T);
}

/** Produces a JSON Schema property key from a label: `Annual turnover (AED)` → `annualTurnoverAed`. */
export function keyFromLabel(label: string): string {
  const words = label
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "field";
  const [first, ...rest] = words;
  const camel = first!.toLowerCase() + rest.map((w) => w[0]!.toUpperCase() + w.slice(1).toLowerCase()).join("");
  return /^[a-zA-Z_]/.test(camel) ? camel : `f_${camel}`;
}

/** Produces a lowercase kebab-case resource key from a label: `Market Study (GCC)` → `market-study-gcc`. Empty when the label has no Latin letters or digits. */
export function slugFromLabel(label: string, max = 64): string {
  const slug = label.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const keyed = !slug || /^[a-z]/.test(slug) ? slug : `k-${slug}`;
  return keyed.slice(0, max).replace(/-+$/, "");
}

/** Unique key inside a collection: `parties`, `parties2`, `parties3`… */
export function uniqueKey(base: string, taken: Iterable<string>): string {
  const set = new Set(taken);
  if (!set.has(base)) return base;
  let i = 2;
  while (set.has(`${base}${i}`)) i++;
  return `${base}${i}`;
}

/** Whether a UI field placement renders an input (vs. static content). */
export function isInput(f: UiField): boolean {
  return !f.content;
}

/** Empty, valid documents used by the builder's "blank" template. */
export function emptySchema(): FormSchema {
  return { $schema: "https://json-schema.org/draft/2020-12/schema", type: "object", properties: {}, required: [] };
}

export function emptyUiSchema(): UiSchema {
  return { steps: [{ key: "details", title: { en: "Details", ar: "التفاصيل" }, sections: [{ key: "main", columns: 2, fields: [] }] }], layout: { showStepper: true } };
}
